/**
 * Static Career Intent catalog for M360 consultant surfaces.
 *
 * Source of truth: membership-mobile-app
 * `src/screens/CareerPlanScreen/careerIntentCatalog.ts`
 * (copied labels/ids; do not invent A/B titles).
 *
 * Persistence note: member writes persist targetPositions as **role titles**
 * via getRoleLabel (not classification codes). Read path is tolerant of codes OR titles.
 * Collection order is rank for improvementAreas, targetPositions, locations,
 * facilityTypes, and motivations (index 0 = most important). Do not sort those arrays.
 */

export type CareerIntentOption<Value extends string = string> = {
  value: Value;
  label: string;
  hint?: string;
};

export type CareerIntentId = "improve" | "new_role" | "location" | "facility" | "retirement" | "none_for_now";

export const CAREER_INTENT_OPTIONS: CareerIntentOption<CareerIntentId>[] = [
  {
    value: "improve",
    label: "Improving my current role",
    hint: "Same title — different hours, pay, responsibilities, etc."
  },
  {
    value: "new_role",
    label: "Pursuing a different or higher position",
    hint: "Including a promotion at my current facility"
  },
  {
    value: "location",
    label: "Exploring relocation",
    hint: "Open to other states"
  },
  {
    value: "facility",
    label: "Exploring other types of facilities",
    hint: "Private, public, academy, indoor, etc."
  },
  {
    value: "retirement",
    label: "Thinking about retirement",
    hint: "Full, partial, or not sure yet — and when"
  },
  {
    value: "none_for_now",
    label: "Nothing major right now",
    hint: "I’m in a good spot"
  }
];

/** Staff-facing direction labels (neutral third-person; member options stay first-person). */
export const CONSULTANT_DIRECTION_LABELS: Record<CareerIntentId, string> = {
  improve: "Improving current role",
  new_role: "Different or higher position",
  location: "Relocation",
  facility: "Other facility types",
  retirement: "Retirement",
  none_for_now: "No major changes"
};

export const NO_DESIRED_CHANGES_CONSULTANT_LABEL = CONSULTANT_DIRECTION_LABELS.none_for_now;

export const IMPROVEMENT_AREA_OPTIONS: CareerIntentOption[] = [
  { value: "hours", label: "Schedule, hours, or flexibility" },
  { value: "comp", label: "Pay, benefits, or compensation" },
  { value: "scope", label: "More or different responsibility" },
  { value: "autonomy", label: "More autonomy in decision-making" },
  { value: "skills", label: "Skills, confidence, or effectiveness" },
  { value: "training", label: "Training or mentorship" },
  { value: "culture", label: "Team, culture, or relationships with leadership" },
  { value: "resources", label: "Staffing, resources, or budget" }
];

export const FACILITY_TYPE_OPTIONS: CareerIntentOption[] = [
  { value: "private_club", label: "Private club" },
  { value: "public_daily_fee", label: "Public / daily-fee" },
  { value: "resort_destination", label: "Resort / destination" },
  { value: "municipal_university", label: "Municipal / university" },
  { value: "practice_indoor_simulator", label: "Practice / indoor / simulator" },
  { value: "golf_entertainment", label: "Golf entertainment" },
  { value: "manufacturer_vendor_management", label: "Manufacturer / vendor / mgmt co" },
  { value: "pga_section_governing_body", label: "PGA Section / governing body" }
];

export const MOTIVATION_OPTIONS: CareerIntentOption[] = [
  { value: "location_family", label: "Location, family, or personal reasons" },
  { value: "money", label: "Pay, benefits or financial goals" },
  { value: "hours", label: "Hours, schedule, or work-life balance" },
  { value: "facility", label: "Facility type or work setting" },
  { value: "title", label: "Role, title, or type of work" },
  { value: "growth", label: "Growth, advancement, or mentorship" },
  { value: "team", label: "Team, culture, leadership, or ownership" },
  { value: "opportunity", label: "A specific opportunity or connection" },
  { value: "stability", label: "Stability concerns or changes at current employer" }
];

export const RETIREMENT_TYPE_OPTIONS: CareerIntentOption[] = [
  { value: "full", label: "Full retirement", hint: "Stepping away from work" },
  { value: "partial", label: "Partial retirement", hint: "Fewer hours, consulting, or a lighter role" },
  { value: "uncertain", label: "Not sure yet" }
];

export const TIMEFRAME_OPTIONS: CareerIntentOption[] = [
  { value: "under_3_months", label: "Less than 3 months" },
  { value: "3_6_months", label: "3–6 months" },
  { value: "6_12_months", label: "6–12 months" },
  { value: "1_2_years", label: "1–2 years" },
  { value: "3_5_years", label: "3–5 years" },
  { value: "5_plus_years", label: "5+ years" }
];

export const CLARITY_OPTIONS: CareerIntentOption[] = [
  { value: "1", label: "Not clear at all" },
  { value: "2", label: "Slightly clear" },
  { value: "3", label: "Somewhat clear" },
  { value: "4", label: "Mostly clear" },
  { value: "5", label: "Very clear" }
];

export type CareerRoleOption = {
  value: string;
  label: string;
};

export const CAREER_ROLE_OPTIONS: CareerRoleOption[] = [
  { value: "A-7", label: "Assistant GM / Membership Director / CFO" },
  { value: "A-8", label: "Assistant Golf Professional" },
  { value: "A-23", label: "Club Fitting/Club Repair" },
  { value: "A-12", label: "College / University Golf Coach" },
  { value: "A-4", label: "Director of Golf" },
  { value: "A-14", label: "Director of Instruction" },
  { value: "A-9", label: "Regional Manager or Owner" },
  { value: "A-13", label: "General Manager" },
  { value: "A-11", label: "Golf Administrator" },
  { value: "A-10", label: "Golf Clinician" },
  { value: "A-16", label: "Golf Course Architect" },
  { value: "A-17", label: "Golf Course Superintendent" },
  { value: "A-24", label: "Golf Industry Associate" },
  { value: "A-6", label: "Golf Instructor" },
  { value: "A-19", label: "Golf Manufacturer Management" },
  { value: "A-20", label: "Golf Manufacturer Sales Representative" },
  { value: "A-18", label: "Golf Media" },
  { value: "A-1", label: "Head Professional - Golf Course" },
  { value: "A-2", label: "Head Professional - Range" },
  { value: "A-15", label: "Retail Golf Facility Ownership or Management" },
  { value: "A-22", label: "Rules Official" },
  { value: "A-3", label: "Touring Professional" },
  { value: "A-21", label: "Tournament Director or Coordinator" },
  { value: "B-1", label: "Head Professional - Golf Course" },
  { value: "B-2", label: "Head Professional - Golf Range" },
  { value: "B-4", label: "Director of Golf" },
  { value: "B-6", label: "Golf Instructor" },
  { value: "B-7", label: "Assistant GM / Membership Director / CFO" },
  { value: "B-8", label: "Assistant Golf Professional" },
  { value: "B-9", label: "Regional Manager or Owner" },
  { value: "B-10", label: "Golf Clinician" },
  { value: "B-11", label: "Golf Administrator" },
  { value: "B-12", label: "College / University Golf Coach" },
  { value: "B-13", label: "General Manager" },
  { value: "B-14", label: "Director of Instruction" },
  { value: "B-15", label: "Retail Golf Facility Ownership or Management" },
  { value: "B-17", label: "Golf Course Superintendent" },
  { value: "B-18", label: "Golf Media" },
  { value: "B-19", label: "Golf Manufacturer Management" },
  { value: "B-20", label: "Golf Manufacturer Sales Representative" },
  { value: "B-21", label: "Tournament Director or Coordinator" },
  { value: "B-22", label: "Rules Official" },
  { value: "B-23", label: "Club Fitting/Club Repair" },
  { value: "B-24", label: "Golf Industry Associate" }
];

export type StateOption = CareerIntentOption;

export const US_STATE_OPTIONS: StateOption[] = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" }
];

export const OPEN_TO_ANYWHERE = "open_to_anywhere";

export function normalizeClassification(value?: string | null): string | undefined {
  const match = value?.trim().match(/^([AB])\s*-?\s*(\d+)$/i);
  return match ? `${match[1].toUpperCase()}-${match[2]}` : value?.trim() || undefined;
}

function roleOptionForValue(value?: string | null): CareerRoleOption | undefined {
  const normalizedValue = value?.trim().toLocaleLowerCase();
  const normalizedCode = normalizeClassification(value);

  return CAREER_ROLE_OPTIONS.find(
    (option) => option.value === normalizedCode || option.label.toLocaleLowerCase() === normalizedValue
  );
}

export function getRoleCode(value?: string | null): string | undefined {
  return roleOptionForValue(value)?.value;
}

export function getRoleLabel(value?: string | null): string | undefined {
  return roleOptionForValue(value)?.label;
}

export function getRoleOptionLabel(option: CareerRoleOption): string {
  return option.label;
}

export function getStateLabel(value: string): string | undefined {
  return US_STATE_OPTIONS.find((option) => option.value === value)?.label;
}

export function getOptionLabel(options: CareerIntentOption[], value?: string | null): string | undefined {
  return options.find((option) => option.value === value)?.label;
}

const ROLE_RECOMMENDATIONS: Record<string, string[]> = {
  "A-1": ["A-4", "A-8", "A-6"],
  "A-2": ["A-6", "A-1", "A-8"],
  "A-3": ["A-6", "B-8"],
  "A-4": ["A-1", "A-13", "A-6"],
  "A-6": ["A-8", "A-14", "A-1"],
  "A-7": ["A-1", "A-4", "A-13"],
  "A-8": ["A-1", "A-6", "A-14"],
  "A-9": ["A-13", "A-1", "A-4"],
  "A-10": ["A-6", "A-1", "A-8"],
  "A-11": ["A-8", "A-1", "A-6"],
  "A-12": ["A-6", "A-8", "A-1"],
  "A-13": ["A-1", "A-4", "A-9"],
  "A-14": ["A-6", "A-1", "A-8"],
  "A-15": ["A-6", "A-8", "A-1"],
  "A-16": ["A-6", "A-8", "A-9"],
  "A-17": ["A-8", "A-1", "A-13"],
  "A-18": ["A-14", "A-1", "A-8"],
  "A-19": ["A-20", "A-1", "A-6"],
  "A-20": ["A-8", "A-6", "A-1"],
  "A-21": ["A-8", "A-1", "A-6"],
  "A-22": ["A-8", "A-6", "A-1"],
  "A-23": ["A-6", "A-8", "A-1"],
  "A-24": ["A-8", "A-15", "A-6"],
  "B-1": ["A-1", "B-8", "B-13"],
  "B-2": ["B-8", "A-2", "B-6"],
  "B-4": ["A-4", "B-8", "B-1"],
  "B-6": ["A-6", "B-8", "B-14"],
  "B-7": ["A-7", "B-4", "B-8"],
  "B-8": ["A-8", "B-1", "B-6"],
  "B-9": ["A-9", "B-8", "B-14"],
  "B-10": ["B-8", "A-10", "A-24"],
  "B-11": ["A-11", "B-8", "B-14"],
  "B-12": ["A-12", "B-8", "B-6"],
  "B-13": ["A-13", "B-8", "B-1"],
  "B-14": ["A-14", "B-6", "B-8"],
  "B-15": ["B-8", "A-15", "B-6"],
  "B-17": ["B-8", "A-17", "B-6"],
  "B-18": ["A-18", "B-7", "B-8"],
  "B-19": ["A-19", "B-6", "B-8"],
  "B-20": ["A-20", "B-8", "B-14"],
  "B-21": ["B-8", "A-21", "B-1"],
  "B-22": ["B-23"],
  "B-23": ["B-8", "A-23", "B-6"],
  "B-24": ["B-8", "B-6", "A-24"]
};

function uniqueRoleOptions(options: CareerRoleOption[]): CareerRoleOption[] {
  const seenLabels = new Set<string>();

  return options.filter((option) => {
    const normalizedLabel = option.label.toLocaleLowerCase();
    if (seenLabels.has(normalizedLabel)) {
      return false;
    }

    seenLabels.add(normalizedLabel);
    return true;
  });
}

/** Unique by label, preferring A-class codes that appear first in CAREER_ROLE_OPTIONS. */
export function uniqueTargetPositionOptions(): CareerRoleOption[] {
  return uniqueRoleOptions(CAREER_ROLE_OPTIONS);
}

export function getSuggestedPositions(currentRole?: string | null): CareerRoleOption[] {
  const currentCode = getRoleCode(currentRole);
  const currentLabel = getRoleLabel(currentCode);

  return uniqueRoleOptions(
    (currentCode ? (ROLE_RECOMMENDATIONS[currentCode] ?? []) : [])
      .filter((code) => code !== currentCode)
      .map((code) => CAREER_ROLE_OPTIONS.find((option) => option.value === code))
      .filter((option): option is CareerRoleOption => Boolean(option))
      .filter((option) => option.label.toLocaleLowerCase() !== currentLabel?.toLocaleLowerCase())
  );
}

export function getOtherPositions(currentRole?: string | null): CareerRoleOption[] {
  const currentCode = getRoleCode(currentRole);
  const currentLabel = getRoleLabel(currentCode)?.toLocaleLowerCase();
  const suggestedLabels = new Set(getSuggestedPositions(currentCode).map((option) => option.label.toLocaleLowerCase()));

  return uniqueRoleOptions(
    CAREER_ROLE_OPTIONS.filter(
      (option) =>
        option.value !== currentCode &&
        option.label.toLocaleLowerCase() !== currentLabel &&
        !suggestedLabels.has(option.label.toLocaleLowerCase())
    ).sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: "base" }))
  );
}

export type CatalogOption = { value: string; label: string; hint?: string };

export type CareerIntentCatalogDomain =
  | "direction"
  | "targetPosition"
  | "facilityType"
  | "improvementArea"
  | "motivation"
  | "timeframe"
  | "retirementType"
  | "clarity"
  | "location";

/** Alias member direction options for M360 catalog domain maps. */
export const DIRECTION_OPTIONS: CatalogOption[] = CAREER_INTENT_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
  hint: option.hint
}));

export const LOCATION_OPTIONS: CatalogOption[] = [
  { value: OPEN_TO_ANYWHERE, label: "Open to anywhere" },
  ...US_STATE_OPTIONS
];

function optionsToMap(options: CatalogOption[]): Map<string, string> {
  return new Map(options.map((option) => [option.value, option.label]));
}

const DOMAIN_LABEL_MAPS: Record<CareerIntentCatalogDomain, Map<string, string>> = {
  direction: optionsToMap(DIRECTION_OPTIONS),
  targetPosition: optionsToMap(CAREER_ROLE_OPTIONS),
  facilityType: optionsToMap(FACILITY_TYPE_OPTIONS),
  improvementArea: optionsToMap(IMPROVEMENT_AREA_OPTIONS),
  motivation: optionsToMap(MOTIVATION_OPTIONS),
  timeframe: optionsToMap(TIMEFRAME_OPTIONS),
  retirementType: optionsToMap(RETIREMENT_TYPE_OPTIONS),
  clarity: optionsToMap(CLARITY_OPTIONS),
  location: optionsToMap(LOCATION_OPTIONS)
};

function titleCaseToken(value: string): string {
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Prefer catalog label; fall back to the stored string.
 * targetPositions: resolve member titles OR classification codes via getRoleLabel.
 */
export function formatCatalogValue(
  domain: CareerIntentCatalogDomain,
  value: string | number | null | undefined
): string {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  if (domain === "targetPosition") {
    const roleLabel = getRoleLabel(raw);
    if (roleLabel) return roleLabel;
    // Already a human label from member writes — keep as-is when unrecognized.
    if (/[a-z]/.test(raw) && /\s/.test(raw)) return raw;
  }

  const byCode = DOMAIN_LABEL_MAPS[domain].get(raw);
  if (byCode) return byCode;

  if (domain === "clarity") {
    const byClarity = DOMAIN_LABEL_MAPS.clarity.get(String(Number(raw)));
    if (byClarity) return byClarity;
  }

  if (domain === "location" && raw.toUpperCase() === raw && raw.length === 2) {
    return DOMAIN_LABEL_MAPS.location.get(raw.toUpperCase()) || raw;
  }

  if (/^[A-Za-z0-9]+(_[A-Za-z0-9]+)+$/.test(raw) || /^[A-Z0-9]{2,}$/.test(raw)) {
    return titleCaseToken(raw);
  }

  return raw;
}

export function formatCatalogValues(
  domain: CareerIntentCatalogDomain,
  values: Array<string | number | null | undefined> | null | undefined
): string[] {
  if (!values?.length) return [];
  return values.map((value) => formatCatalogValue(domain, value)).filter(Boolean);
}

export function formatRankedLabels(labels: string[]): string[] {
  if (labels.length < 2) {
    return labels;
  }

  return labels.map((label, index) => `${index + 1}. ${label}`);
}

/** Join summary values so each ranked (or multi-value) answer stays on its own ListItem headline line. */
export function formatSummaryHeadline(values: string[]): string {
  return values.join("\n");
}

export function formatCatalogList(
  domain: CareerIntentCatalogDomain,
  values: Array<string | number | null | undefined> | null | undefined
): string | null {
  const labels = formatCatalogValues(domain, values);
  return labels.length > 0 ? labels.join(", ") : null;
}

export type CareerIntentDirectionFields = {
  selectedIntents?: string[] | null;
  targetPositions?: string[] | null;
  facilityTypes?: string[] | null;
  improvementAreas?: string[] | null;
  retirementType?: string | null;
  locations?: string[] | null;
  hasNoDesiredChanges?: boolean | null;
};

export function resolveSelectedIntents(intent: CareerIntentDirectionFields | null | undefined): CareerIntentId[] {
  if (!intent) return [];
  if (intent.hasNoDesiredChanges) return ["none_for_now"];

  const persisted = (intent.selectedIntents ?? []).filter(
    (value): value is CareerIntentId => value === "none_for_now" || ACTIVE_INTENT_IDS.includes(value as CareerIntentId)
  );
  const inferred = inferDirectionsFromBranchFields(intent);
  if (persisted.length > 0) {
    if (persisted.includes("none_for_now")) return ["none_for_now"];
    return [...persisted, ...inferred.filter((value) => !persisted.includes(value))];
  }

  return inferred;
}

function inferDirectionsFromBranchFields(intent: CareerIntentDirectionFields): CareerIntentId[] {
  const directions: CareerIntentId[] = [];
  if (intent.targetPositions && intent.targetPositions.length > 0) directions.push("new_role");
  if (intent.facilityTypes && intent.facilityTypes.length > 0) directions.push("facility");
  if (intent.improvementAreas && intent.improvementAreas.length > 0) directions.push("improve");
  if (intent.retirementType) directions.push("retirement");
  if (intent.locations && intent.locations.length > 0) directions.push("location");
  return directions;
}

export function inferDirections(intent: CareerIntentDirectionFields | null | undefined): CareerIntentId[] {
  return resolveSelectedIntents(intent);
}

export type CareerIntentSummaryRow = {
  label: string;
  values: string[];
};

export type CareerIntentSummaryGroupConfig = {
  id: string;
  heading?: string;
  labels: readonly string[];
};

export type CareerIntentSummaryFields = CareerIntentDirectionFields & {
  motivations?: string[] | null;
  timeframe?: string | null;
  clarity?: number | null;
  additionalContext?: string | null;
};

const ACTIVE_INTENT_IDS: CareerIntentId[] = ["improve", "new_role", "location", "facility", "retirement"];

/** M360 consultant read labels — third-person/neutral; member-app values stay in catalog option text. */
export const CONSULTANT_SUMMARY_LABELS = {
  exploring: "Exploring",
  improvement: "Areas to Improve",
  targetPositions: "Target Positions",
  states: "States",
  facilityTypes: "Facility Types",
  retirement: "Retirement",
  motivations: "Motivations",
  timeframe: "Timeframe",
  clarity: "Clarity on Next Steps",
  notes: "Additional Context"
} as const;

/**
 * Consultant `/goals` cards under the metric row.
 * Exploring, Timeframe, and Clarity live in the four-across metric row, not here.
 * Unfilled labels stay omitted; empty cards still render so the UI can show EmptyState.
 */
export const CONSULTANT_SUMMARY_CARDS = [
  {
    id: "careerRole",
    heading: "Career Role",
    labels: [
      CONSULTANT_SUMMARY_LABELS.improvement,
      CONSULTANT_SUMMARY_LABELS.targetPositions,
      CONSULTANT_SUMMARY_LABELS.motivations
    ]
  },
  {
    id: "location",
    heading: "Location",
    labels: [CONSULTANT_SUMMARY_LABELS.states, CONSULTANT_SUMMARY_LABELS.facilityTypes]
  },
  {
    id: "additionalDetails",
    heading: "Additional Details",
    labels: [CONSULTANT_SUMMARY_LABELS.retirement, CONSULTANT_SUMMARY_LABELS.notes]
  }
] as const satisfies readonly CareerIntentSummaryGroupConfig[];

export type CareerIntentSummaryCardId = (typeof CONSULTANT_SUMMARY_CARDS)[number]["id"];

export type CareerIntentSummaryCard = {
  id: CareerIntentSummaryCardId;
  heading: string;
  rows: CareerIntentSummaryRow[];
};

export function getCareerIntentSummaryCards(rows: CareerIntentSummaryRow[]): CareerIntentSummaryCard[] {
  return CONSULTANT_SUMMARY_CARDS.map((card) => ({
    id: card.id,
    heading: card.heading,
    rows: card.labels.flatMap((label) => rows.filter((row) => row.label === label))
  }));
}

/**
 * Career Intent summary rows for M360 consultant read surfaces.
 * Row labels use staff-facing copy; option values follow the member-app catalog.
 */
export function getCareerIntentSummaryRows(
  goal: CareerIntentSummaryFields | null | undefined,
  includeNotes = false
): CareerIntentSummaryRow[] {
  if (!goal) return [];

  const rows: CareerIntentSummaryRow[] = [];
  const selectedIntents = resolveSelectedIntents(goal);
  const active = selectedIntents.filter((intent) => ACTIVE_INTENT_IDS.includes(intent));

  if (selectedIntents.length > 0) {
    rows.push({
      label: CONSULTANT_SUMMARY_LABELS.exploring,
      values: formatRankedLabels(selectedIntents.map((intent) => CONSULTANT_DIRECTION_LABELS[intent]))
    });
  }

  if (active.includes("improve") && goal.improvementAreas?.length) {
    rows.push({
      label: CONSULTANT_SUMMARY_LABELS.improvement,
      values: formatRankedLabels(formatCatalogValues("improvementArea", goal.improvementAreas))
    });
  }

  if (active.includes("new_role") && goal.targetPositions?.length) {
    rows.push({
      label: CONSULTANT_SUMMARY_LABELS.targetPositions,
      values: formatRankedLabels(formatCatalogValues("targetPosition", goal.targetPositions))
    });
  }

  if (active.includes("location") && goal.locations?.length) {
    rows.push({
      label: CONSULTANT_SUMMARY_LABELS.states,
      values: formatRankedLabels(formatCatalogValues("location", goal.locations))
    });
  }

  if (active.includes("facility") && goal.facilityTypes?.length) {
    rows.push({
      label: CONSULTANT_SUMMARY_LABELS.facilityTypes,
      values: formatRankedLabels(formatCatalogValues("facilityType", goal.facilityTypes))
    });
  }

  if (active.includes("retirement") && goal.retirementType) {
    const retirementLabel = formatCatalogValue("retirementType", goal.retirementType);
    if (retirementLabel) {
      rows.push({ label: CONSULTANT_SUMMARY_LABELS.retirement, values: [retirementLabel] });
    }
  }

  if (active.length > 0 && goal.motivations?.length) {
    rows.push({
      label: CONSULTANT_SUMMARY_LABELS.motivations,
      values: formatRankedLabels(formatCatalogValues("motivation", goal.motivations))
    });
  }

  if (active.length > 0 && goal.timeframe) {
    const timeframeLabel = formatCatalogValue("timeframe", goal.timeframe);
    if (timeframeLabel) {
      rows.push({ label: CONSULTANT_SUMMARY_LABELS.timeframe, values: [timeframeLabel] });
    }
  }

  if (active.length > 0 && goal.clarity != null) {
    const clarityLabel = formatCatalogValue("clarity", goal.clarity);
    if (clarityLabel) {
      rows.push({ label: CONSULTANT_SUMMARY_LABELS.clarity, values: [clarityLabel] });
    }
  }

  const notes = goal.additionalContext?.trim();
  if (includeNotes && notes) {
    rows.push({ label: CONSULTANT_SUMMARY_LABELS.notes, values: [notes] });
  }

  return rows;
}
